import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  getPromocodeItem,
  createPromocode,
  updatePromocode,
  getBonusMaskList,
  updateBonusMask,
  createBonusMask,
  BonusMaskItem,
} from "store/liveOps/promocodes/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import Button from "components/buttons/Button";
import { Modal } from "components/Modal/Modal";
import styles from "./LiveOpsPromocodesEditModal.module.scss";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import { ModalPromocodesTabs } from "configs/types";
import PromocodesMainTab from "../tabs/promocodes/PromocodesMainTab";
import PromocodesMaskTab from "../tabs/promocodes/PromocodesMaskTab";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const LiveOpsPromocodesEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const formErrors = useFormErrors();

  const isItemFetched = useAppSelector(
    (store) => store.promocodes.isItemFetched,
  );
  const payload = useAppSelector((store) => store.promocodes.payload);
  const isItemFetching = useAppSelector(
    (store) => store.promocodes.isItemFetching,
  );
  const isBonusMaskLoading = useAppSelector(
    (store) => store.promocodes.isBonusMaskLoading,
  );
  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const bonusMask = useAppSelector((store) => store.promocodes.bonusMask);
  const selectedBonusId = useAppSelector(
    (store) => store.promocodes.selectedBonusId,
  );
  const isEdit = Boolean(entityId || payload?.id);
  const isEditMode = searchParams.get("entityId") !== null;
  const [tabs] = useState<ModalPromocodesTabs[]>([
    ModalPromocodesTabs.main,
    ModalPromocodesTabs.mask,
  ]);
  const [activeTab, setActiveTab] = useState<ModalPromocodesTabs>(
    ModalPromocodesTabs.main,
  );

  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([
    "EUR",
  ]);
  const [initialBonusMask, setInitialBonusMask] = useState<
    BonusMaskItem | undefined
  >();
  const isMaskDirty =
    JSON.stringify(initialBonusMask) !== JSON.stringify(bonusMask);

    

  const handleCurrencyTabClick = (currency: string) => {
    if (!bonusMask) {
      setSelectedCurrencies([currency]);

      return;
    }

    if (isMaskDirty) {

      dispatch(
        showNotification({
          title: t("warning"),
          message: t("saveChangesFirst"),
          type: NotificationTypes.warning,
        }),
      );

      return;
    }

    setSelectedCurrencies([currency]);
  };

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (bonusMask?.currency) {
      setSelectedCurrencies([bonusMask.currency]);
    }
  }, [bonusMask?.currency]);

  useEffect(() => {
    if (!bonusMask) {
      return;
    }

    setInitialBonusMask(structuredClone(bonusMask));
  }, [bonusMask?.id]);

  useEffect(() => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      dispatch(getPromocodeItem(searchParams.get("entityId") ?? ""));
    }
  }, [searchParams, isItemFetched]);

  useEffect(() => {
    const promocodeId = searchParams.get("entityId");

    if (!promocodeId || !selectedCurrencies[0]) {
      return;
    }

    dispatch(
      getBonusMaskList({
        limit: 1,
        offset: 0,
        promocodeId: Number(promocodeId),
        currency: selectedCurrencies[0],
      }),
    );
  }, [dispatch, searchParams, selectedCurrencies]);

  const handleTargetBonusChange = (targetBonusId: number) => {
    const promocodeId = searchParams.get("entityId");

    if (!promocodeId || !selectedCurrencies[0]) {
      return;
    }

    dispatch(
      getBonusMaskList({
        limit: 1,
        offset: 0,
        promocodeId: Number(promocodeId),
        targetBonusId,
        currency: selectedCurrencies[0],
      }),
    );
  };

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    const isEditMode = searchParams.get("entityId") !== null;

    try {
      if (activeTab === ModalPromocodesTabs.mask) {
        if (!selectedBonusId || !payload?.id) {
          dispatch(
            showNotification({
              title: t("warning"),
              message: t("selectBonus"),
              type: NotificationTypes.warning,
            }),
          );

          return;
        }

        const maskPayload = {
          ...bonusMask,
          promocodeId: payload.id,
          targetBonusId: selectedBonusId,
          currency: selectedCurrencies[0],
          activationArea: "bonus",
          isActive: 1,
        };

        if (bonusMask?.id) {
          await dispatch(
            updateBonusMask({
              ...maskPayload,
              id: bonusMask.id,
            }),
          ).unwrap();
          setInitialBonusMask(
            structuredClone({
              ...maskPayload,
              isActive: true,
            }),
          );
        } else {
          await dispatch(createBonusMask(maskPayload)).unwrap();
          setInitialBonusMask(
            structuredClone({
              ...maskPayload,
              isActive: true,
            }),
          );
        }

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        return;
      }

      if (!payload) {
        return;
      }

      if (isEditMode) {
        await dispatch(updatePromocode(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const newId = await dispatch(createPromocode(payload)).unwrap();

        dispatch(
          handleUpdateFields({
            ...payload,
            id: newId,
          }),
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        navigate(
          `${location.pathname}?activeModal=promoCode&entityId=${newId}`,
        );
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const renderTab = () => {
    if (activeTab === ModalPromocodesTabs.main) {
      return (
        <PromocodesMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isEditMode={isEditMode}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalPromocodesTabs.mask) {
      if (isBonusMaskLoading) {
        return <ModalTabSkeleton />;
      }

      return (
        <PromocodesMaskTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          selectedCurrencies={selectedCurrencies}
          onTargetBonusChange={handleTargetBonusChange}
          readOnly={!permissions?.update}
        />
      );
    }
    return <div className="promocode-form-wrapper"></div>;
  };

  const handleTabClick = (e: ModalPromocodesTabs) => {
    if (!isEdit && e !== ModalPromocodesTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(e);
  };

  return (
    <Modal onClose={handleCloseClick} size="sm">
      <Modal.Header>{t("promocode")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
          />

          {activeTab === ModalPromocodesTabs.mask && (
            <Modal.Tabs
              tabs={currenciesList}
              activeTab={selectedCurrencies[0]}
              onClick={handleCurrencyTabClick}
              isCurrencyTabs
              isSmallModifier
              className={styles.liveopsPromocodesEditModalCurrencyTabs}
            />
          )}

          <Modal.Content
            className={styles.liveopsPromoCodesEditModalContentWrapper}
          >
            {renderTab()}
          </Modal.Content>

          {permissions?.update && (
            <Modal.Footer>
              {activeTab === ModalPromocodesTabs.mask &&
              isBonusMaskLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />

                  <Button
                    isPrimary
                    handleClick={handleApply}
                    txt={t("save")}
                    isLoad={isItemFetching}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default LiveOpsPromocodesEditModal;
